import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import habitService from '../services/habitService';

const AchievementsPanel = ({ onAchievementEarned }) => {
  const [achievements, setAchievements] = useState([]);
  const [earnedAchievements, setEarnedAchievements] = useState([]);
  const [showUnlocked, setShowUnlocked] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = () => {
    const allAchievements = habitService.getAchievements();
    const habitData = habitService.getHabitData();
    
    setAchievements(allAchievements);
    setEarnedAchievements(habitData.achievements || []);
  };

  const isAchievementEarned = (achievementId) => {
    return earnedAchievements.includes(achievementId);
  };

  const getProgressForAchievement = (achievement) => {
    const habitData = habitService.getHabitData();
    
    switch (achievement.type) {
      case 'streak':
        return Math.min(habitData.currentStreak / achievement.requirement * 100, 100);
      case 'total_days':
        return Math.min(habitData.totalStudyDays / achievement.requirement * 100, 100);
      case 'longest_streak':
        return Math.min(habitData.longestStreak / achievement.requirement * 100, 100);
      case 'weekly_goal':
        return habitService.hasMetWeeklyGoal(habitData, achievement.requirement) ? 100 : 0;
      default:
        return 0;
    }
  };

  const getCurrentValue = (achievement) => {
    const habitData = habitService.getHabitData();
    
    switch (achievement.type) {
      case 'streak':
        return habitData.currentStreak;
      case 'total_days':
        return habitData.totalStudyDays;
      case 'longest_streak':
        return habitData.longestStreak;
      case 'weekly_goal':
        return habitService.hasMetWeeklyGoal(habitData, achievement.requirement) ? 'Completed' : 'In Progress';
      default:
        return 0;
    }
  };

  const handleAchievementClick = (achievement) => {
    setSelectedAchievement(achievement);
  };

  const earnedCount = earnedAchievements.length;
  const totalCount = achievements.length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <TrophyIcon className="w-6 h-6" />
              Achievements
            </h3>
            <p className="text-yellow-100 mt-1">
              {earnedCount} of {totalCount} unlocked
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold">
              {Math.round((earnedCount / totalCount) * 100)}%
            </div>
            <div className="text-sm text-yellow-100">
              Complete
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-yellow-200/30 rounded-full h-2">
            <motion.div 
              className="bg-white h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(earnedCount / totalCount) * 100}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <button
            onClick={() => setShowUnlocked(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
              !showUnlocked
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            All ({totalCount})
          </button>
          <button
            onClick={() => setShowUnlocked(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
              showUnlocked
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            Earned ({earnedCount})
          </button>
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements
            .filter(achievement => !showUnlocked || isAchievementEarned(achievement.id))
            .map((achievement) => {
              const isEarned = isAchievementEarned(achievement.id);
              const progress = getProgressForAchievement(achievement);
              const currentValue = getCurrentValue(achievement);
              
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  className={`relative p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                    isEarned
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                      : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                  onClick={() => handleAchievementClick(achievement)}
                >
                  {/* Achievement Icon */}
                  <div className="flex items-center justify-between mb-3">
                    <div className={`text-3xl ${isEarned ? '' : 'opacity-50'}`}>
                      {achievement.icon}
                    </div>
                    
                    {isEarned ? (
                      <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                        <CheckIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Earned</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <LockIcon className="w-4 h-4" />
                        <span className="text-sm">Locked</span>
                      </div>
                    )}
                  </div>

                  {/* Achievement Details */}
                  <div className="space-y-2">
                    <h4 className={`font-semibold ${
                      isEarned 
                        ? 'text-green-800 dark:text-green-200' 
                        : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      {achievement.title}
                    </h4>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {achievement.description}
                    </p>

                    {/* Progress Section */}
                    {!isEarned && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>Progress</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <motion.div 
                            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {currentValue} / {achievement.requirement}
                        </div>
                      </div>
                    )}

                    {/* Reward */}
                    <div className="flex items-center gap-2">
                      <StarIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                        +{achievement.coins} coins
                      </span>
                    </div>
                  </div>

                  {/* Shine effect for earned achievements */}
                  {isEarned && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  )}
                </motion.div>
              );
            })}
        </div>

        {/* Empty State */}
        {achievements.filter(achievement => !showUnlocked || isAchievementEarned(achievement.id)).length === 0 && (
          <div className="text-center py-8">
            <TrophyIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {showUnlocked ? 'No achievements earned yet' : 'No achievements available'}
            </p>
          </div>
        )}
      </div>

      {/* Achievement Detail Modal */}
      <AnimatePresence>
        {selectedAchievement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedAchievement(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">
                  {selectedAchievement.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                  {selectedAchievement.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {selectedAchievement.description}
                </p>

                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center gap-2">
                    <StarIcon className="w-5 h-5 text-yellow-500" />
                    <span className="font-semibold text-yellow-700 dark:text-yellow-300">
                      Reward: {selectedAchievement.coins} coins
                    </span>
                  </div>
                </div>

                {isAchievementEarned(selectedAchievement.id) ? (
                  <div className="bg-green-100 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-300">
                      <CheckIcon className="w-5 h-5" />
                      <span className="font-medium">Achievement Unlocked!</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-100 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      Keep studying to unlock this achievement!
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setSelectedAchievement(null)}
                  className="mt-4 px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AchievementsPanel; 